import { LoadingOutlined } from '@ant-design/icons';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * The button, ours rather than antd's.
 *
 * Two things made this worth owning. antd's `type` names a visual weight
 * ("primary", "default", "dashed") but its variants are not a scale — `text`
 * and `link` differ by colour alone, and "default" is the outlined one — so the
 * name never told you how loud a button was. And every deviation we wanted
 * arrived as a `className` override with `!` on it, which is a theme fighting
 * itself.
 *
 * The variants here are a ladder, loudest first: `primary` for the one action a
 * screen is for, `secondary` for everything ordinary, `ghost` for toolbar and
 * icon work, `danger` for destruction. If two primaries end up on one screen,
 * that is the screen telling you it does two things.
 *
 * It MUST forward its ref and spread unknown props: antd's Popconfirm, Dropdown
 * and Tooltip clone their child to inject `onClick`, `aria-*` and a ref, and a
 * button that swallows those silently stops working inside them.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the icon for a spinner and blocks the click, keeping the width. */
  loading?: boolean;
  icon?: ReactNode;
  /** Trailing rather than leading — for "Next →", never for a noun. */
  iconEnd?: ReactNode;
  block?: boolean;
  /** `type` on the DOM element. Named apart so `variant` can own the word. */
  htmlType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconEnd,
    block = false,
    htmlType = 'button',
    disabled,
    className = '',
    children,
    ...rest
  },
  ref,
) {
  const iconOnly = !children && (icon || loading);

  return (
    <button
      {...rest}
      ref={ref}
      type={htmlType}
      // A loading button is not disabled — a disabled one loses its tooltip and
      // its focus — so the click is blocked here instead.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={[
        'btn',
        `btn-${variant}`,
        `btn-${size}`,
        block ? 'btn-block' : '',
        iconOnly ? 'btn-icon-only' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading ? (
        <LoadingOutlined className="btn-glyph" />
      ) : icon ? (
        <span className="btn-glyph">{icon}</span>
      ) : null}
      {children ? <span className="btn-label">{children}</span> : null}
      {iconEnd && !loading ? <span className="btn-glyph">{iconEnd}</span> : null}
    </button>
  );
});
